import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  OnInit,
  SimpleChanges,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { NgbDate, NgbDateParserFormatter, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { IDateFilter, DateService } from '@portal/shared/helpers';
import {
  FilterContentItem,
  FILTER_CONTENT_ITEM,
  IFilterToApply,
  ISearchCriteria,
  ISelectInput,
  SearchSelector,
} from '@portal/shared/ui/filter/src';
import { endOfDay, isAfter, startOfDay } from 'date-fns';
import { Observable } from 'rxjs';
import { QuickPicks } from '../enums/date.enum';
declare const $localize;

@Component({
    selector: 'portal-dashboard-date-range',
    templateUrl: './date-range.component.html',
    providers: [
        {
            provide: FILTER_CONTENT_ITEM,
            useExisting: DateRangeComponent,
        },
    ],
    standalone: false
})
export class DateRangeComponent extends FilterContentItem implements OnInit, OnChanges {
  @Output()
  filterApplied: EventEmitter<IFilterToApply[]> = new EventEmitter();

  @Output()
  filterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();

  @Output()
  quickPickApplied: EventEmitter<QuickPicks> = new EventEmitter();

  @Output()
  checkedUpdated: EventEmitter<boolean> = new EventEmitter();

  @Output()
  customSelection: EventEmitter<IDateFilter> = new EventEmitter();

  @Output()
  fromDateSelection: EventEmitter<Date> = new EventEmitter();

  @Output() closeSubmenus: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  selectedEntities: Map<string, ISearchCriteria> = new Map();

  @Input()
  quickPicks: ISelectInput[];

  @Input()
  isCheckboxDisabled: boolean;

  @Input()
  labelName: string;

  @Input()
  isCompareType = false;

  @Input()
  markDisabled: (date: NgbDate) => boolean;

  @Input()
  isCheckedDefault = true;

  @Input()
  isDateRangesOverlapping = false;

  @Input()
  isValidDiffLength = true;

  @Input()
  isValidDateFrom = true;

  @Input()
  isNewDesignFilter = false;

  @Input() showTimePicker = false;

  @Input() dateRangeSelection: IDateFilter;

  @Input() showQuickPicks: boolean;

  @ViewChild('dateFilter') dateFilter: ElementRef;

  selector: string;

  @Output() itemToggled: EventEmitter<number> = new EventEmitter();
  selectedQuickPick: QuickPicks = QuickPicks.Today;
  isOpen = false;
  fromDate: NgbDate;
  toDate: NgbDate;

  fromTime: NgbTimeStruct;
  toTime: NgbTimeStruct;

  dateHeaderText = $localize`Date`;

  isQuickPickApplied: boolean;
  isInvalidDate = false;
  isInvalidTime = false;
  constructor(
    public formatter: NgbDateParserFormatter,
    private dateService: DateService,
    private elementRef: ElementRef,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.isCompareType) {
      this.selector = SearchSelector.CompareTo;
    } else {
      this.selector = SearchSelector.DateRange;
    }
    this.isQuickPickApplied = false;
    this.setSelectedEntities(this.selectedEntities);
    this.updateDateHeaderText();
  }

  setAutoFocusState(): void {
    this.dateFilter.nativeElement.focus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['selectedEntities'];
    if (change && !change.firstChange) {
      this.setSelectedEntities(change.currentValue);
      this.updateDateHeaderText();
    }
  }

  getList(): Observable<ISelectInput[]> {
    return null;
  }

  areDatesValid(): boolean {
    return (
      !this.isInvalidDate &&
      !this.isInvalidTime &&
      Boolean(this.toDate && this.fromDate) &&
      !this.isDateRangesOverlapping &&
      this.isValidDiffLength &&
      this.isValidDateFrom
    );
  }

  updateIsInvalidDate(date: boolean): void {
    this.isInvalidDate = date;
  }

  updateIsInvalidTime(isValid: boolean): void {
    this.isInvalidTime = isValid;
  }

  onFromDateApplied(fromDate: NgbDate): void {
    this.fromDate = fromDate;
    this.fromDateSelection.emit(
      new Date(
        this.fromDate.year,
        this.fromDate.month - 1,
        this.fromDate.day,
        this.fromTime?.hour || 0,
        this.fromTime?.minute || 0,
        this.fromTime?.second || 0,
        0,
      ),
    );
  }

  onToDateApplied(toDate: NgbDate): void {
    this.toDate = toDate;
    if (this.fromDate && this.toDate) {
      this.customSelection.emit({
        start: new Date(
          this.fromDate.year,
          this.fromDate.month - 1,
          this.fromDate.day,
          this.fromTime?.hour || 0,
          this.fromTime?.minute || 0,
          this.fromTime?.second || 0,
          0,
        ).toISOString(),
        end: new Date(
          this.toDate.year,
          this.toDate.month - 1,
          this.toDate.day,
          this.toTime?.hour ?? 23,
          this.toTime?.minute ?? 59,
          this.toTime?.second ?? 59,
          999,
        ).toISOString(),
      });
    }
  }

  onFromTimeApplied(fromTime: NgbTimeStruct): void {
    if (this.showTimePicker) {
      this.fromTime = fromTime;
      this.onFromDateApplied(this.fromDate);
    }
  }

  onToTimeApplied(toTime: NgbTimeStruct): void {
    if (this.showTimePicker) {
      this.toTime = toTime;
      this.onToDateApplied(this.toDate);
    }
  }

  onFilterApplied(): void {
    const dateRangeFilters = [];
    const toDate = new Date(
      this.toDate.year,
      this.toDate.month - 1,
      this.toDate.day,
      this.toTime?.hour ?? 23,
      this.toTime?.minute ?? 59,
      this.toTime?.second ?? 59,
      999,
    );

    dateRangeFilters.push({
      key: `${this.selector}_${SearchSelector.FromDate}`,
      operator: '=',
      argument: new Date(
        this.fromDate.year,
        this.fromDate.month - 1,
        this.fromDate.day,
        this.fromTime?.hour || 0,
        this.fromTime?.minute || 0,
        this.fromTime?.second || 0,
        0,
      ).toISOString(),
      value: [],
      selector: SearchSelector.FromDate,
    });
    dateRangeFilters.push({
      key: `${this.selector}_${SearchSelector.ToDate}`,
      operator: '=',
      argument: toDate.toISOString(),
      value: [],
      selector: SearchSelector.ToDate,
    });

    this.updateDateHeaderText();
    this.filterApplied.emit(dateRangeFilters);
    this.toggleOpen();
  }

  onCheckboxToggle(): void {
    this.isCheckedDefault = !this.isCheckedDefault;
    this.updateDateHeaderText();
    this.isOpen = false;
    this.checkedUpdated.emit(this.isCheckedDefault);
    if (!this.isCheckedDefault && this.isOpen) {
      this.itemToggled.emit();
    }
  }

  onQuickPickApplied(value: QuickPicks): void {
    this.selectedQuickPick = value;
    this.isQuickPickApplied = true;
    this.quickPickApplied.emit(value);
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    this.itemToggled.emit();
  }

  onKeyDownSpace(event: Event): void {
    this.isOpen = !this.isOpen;
    event.preventDefault();
  }

  onKeyDownSpaceApply(event: Event): void {
    this.onFilterApplied();
    event.preventDefault();
  }

  private setSelectedEntities(selectedEntities: Map<string, ISearchCriteria>): void {
    this.selectedEntities = selectedEntities;
    if (this.selectedEntities && this.selectedEntities.size) {
      Array.from(selectedEntities.values()).forEach((input: ISearchCriteria) => {
        const date = new Date(input.argument);
        const dateAfterFormatted = NgbDate.from({
          day: date.getDate(),
          month: date.getMonth() + 1,
          year: date.getFullYear(),
        });
        const timeFormatted: NgbTimeStruct = {
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
        };
        if (input.selector.includes('from')) {
          this.fromDate = dateAfterFormatted;
          this.fromTime = timeFormatted;
        } else {
          this.toDate = dateAfterFormatted;
          this.toTime = timeFormatted;
        }
      });
    }

    if (this.isCompareType && this.isCheckedDefault && !this.isQuickPickApplied) {
      this.updateDateHeaderText();
    }
    this.isQuickPickApplied = false;
  }

  private updateDateHeaderText(): void {
    if (this.isCompareType && !this.isCheckedDefault) {
      this.dateHeaderText = `${this.dateService.getPlaceholder()} - ${this.dateService.getPlaceholder()}`;
      return;
    }

    if (this.fromDate?.equals(this.toDate)) {
      this.dateHeaderText = `${this.formatter.format(this.fromDate)} ${this.dateService.padZero(
        this.fromTime?.hour ?? 0,
      )}:${this.dateService.padZero(this.fromTime?.minute ?? 0)} - ${this.dateService.padZero(
        this.toTime?.hour ?? 23,
      )}:${this.dateService.padZero(this.toTime?.minute ?? 59)}`;
    } else {
      this.dateHeaderText = `${this.formatter.format(this.fromDate)} ${this.dateService.padZero(
        this.fromTime?.hour ?? 0,
      )}:${this.dateService.padZero(this.fromTime?.minute ?? 0)} - ${this.formatter.format(
        this.toDate,
      )} ${this.dateService.padZero(this.toTime?.hour ?? 23)}:${this.dateService.padZero(
        this.toTime?.minute ?? 59,
      )}`;
    }
  }
}
